/**
 * First-system onboarding intake — V98-3-6 (PRD G-8, ADR-0048 §5, research §8).
 *
 * Onboarding asks at most three new questions at first-system time — the
 * system owner, which programs cover it (with the SOC 2 report type I / II),
 * and whether it handles federal government data — and confirms the rest
 * (internet reachability, model providers) from what the scan derived. This
 * service persists those answers through the existing services, so no
 * onboarding-only write path exists:
 *
 *   owner      → `system_owner` assignment on the system (assignment-service;
 *                a changed answer ends the previous holder's hat).
 *   programs   → the workspace's active program per key (certification-service;
 *                created when missing — FedRAMP CR26 on the 20x path) and a
 *                standing boundary membership for the system (boundary-service).
 *                An existing program is never re-typed: a SOC 2 program that is
 *                already Type I stays Type I (reported back as `existing`).
 *                No audit period is opened here: its window is a date the
 *                program lead sets in Scope, and the standing boundary applies
 *                to every period until one has rows of its own (ADR-0048 §4).
 *   facts      → `ai_systems.handles_federal_data / internet_reachable /
 *                providers_json` (boundary-service setSystemFacts). "Not sure"
 *                stores NULL (unanswered) and is asked of Legal.
 *   law answers, PHI, federal "not sure"
 *              → ONE open onboarding PROPOSAL in `obligation_source_decisions`
 *                (V98-4-1, migration 237): "proposed by <name> · awaiting
 *                Legal", never a decision. A newer answer for the same system
 *                supersedes the open one. AI legislation is proposed on only
 *                when the AI Regulatory program is chosen and the org ceiling
 *                allows it (a `frameworks_only` org gets the facts without the
 *                toggle — onboarding never fails on the ceiling). The legal
 *                reviewer sees it on Today and in the program's Scope tab.
 *
 * Authorization is the route's job (owner / admin of the workspace). Every
 * id is resolved inside `workspace_id`; another workspace's system is
 * not_found. Each step is idempotent, so a retry after a partial failure
 * converges instead of duplicating rows.
 */
import { getProgramManifest, programCreateFields, programHas } from '../programs/manifests.js';
import { generateRandomId } from '../utils/audit-crypto.js';
import { syncOwnerAssignment } from './assignment-service.js';
import { addToBoundary, BoundaryError, setSystemFacts } from './boundary-service.js';
import { createCertificationProfile, getActiveProfile, } from './certification-service.js';
import { aiProgramEvent, createProposal, getCeiling, resolveLegalReviewer as resolveReviewer } from './obligation-source-service.js';
export const ONBOARDING_DECISION_DOMAINS = ['hiring', 'care', 'coverage', 'credit', 'none'];
/** Jurisdictions named consistently: New York City and New York State are separate (AEDT vs state law). */
export const ONBOARDING_STATES = [
    { value: 'ca', label: 'California' },
    { value: 'co', label: 'Colorado' },
    { value: 'nyc', label: 'New York City' },
    { value: 'ny', label: 'New York State' },
    { value: 'tx', label: 'Texas' },
    { value: 'il', label: 'Illinois' },
    { value: 'other_us', label: 'Other US states' },
];
export const ONBOARDING_EU_ROLES = ['provider', 'deployer', 'both', 'ask_legal'];
export class OnboardingIntakeError extends Error {
    code;
    constructor(code, message) {
        super(message);
        this.code = code;
        this.name = 'OnboardingIntakeError';
    }
}
const DOMAIN_LABEL = {
    hiring: 'Hiring', care: 'Care', coverage: 'Coverage', credit: 'Credit', none: 'None',
};
const EU_ROLE_LABEL = {
    provider: 'Provider', deployer: 'Deployer', both: 'Both provider and deployer', ask_legal: 'Not sure — Legal to decide',
};
const STATE_LABEL = new Map(ONBOARDING_STATES.map((s) => [s.value, s.label]));
export const ONBOARDING_PROPOSAL_SOURCE = 'onboarding_proposal';
/**
 * The lines Legal sees on the proposal, or [] when nothing needs Legal.
 * Pure — the SPA uses the same rules to show "Legal proposal" tags.
 */
export function legalProposalLines(input) {
    const lines = [];
    const law = input.law ?? {};
    if (law.phi)
        lines.push('Health data (PHI): yes, from the onboarding intake');
    if (input.federal_data === 'unsure')
        lines.push('Federal government data: not sure');
    const domains = (law.decides_about ?? []).filter((d) => ONBOARDING_DECISION_DOMAINS.includes(d));
    if (domains.length)
        lines.push(`Helps decide something about a person: ${domains.map((d) => DOMAIN_LABEL[d]).join(', ')}`);
    const states = (law.states ?? []).filter((s) => STATE_LABEL.has(s));
    if (states.length)
        lines.push(`Where the people it is used on are: ${states.map((s) => STATE_LABEL.get(s)).join(', ')}`);
    if (law.eu_role && ONBOARDING_EU_ROLES.includes(law.eu_role))
        lines.push(`EU role: ${EU_ROLE_LABEL[law.eu_role]}`);
    return lines;
}
async function systemName(db, workspaceId, systemId) {
    const r = await db.query(`SELECT s.name FROM ai_systems s LEFT JOIN customer_profiles p ON p.profile_id = s.profile_id
      WHERE s.system_id = ? AND (s.workspace_id = ? OR (s.workspace_id IS NULL AND p.workspace_id = ?)) LIMIT 1`, [systemId, workspaceId, workspaceId]);
    return r.results[0]?.name ?? null;
}
async function displayName(db, userId) {
    const r = await db.query(`SELECT display_name, email FROM users WHERE user_id = ? LIMIT 1`, [userId]);
    const u = r.results[0];
    return u ? (u.display_name || u.email || null) : null;
}
async function isActiveMember(db, workspaceId, userId) {
    const r = await db.query(`SELECT user_id FROM memberships WHERE workspace_id = ? AND user_id = ? AND removed_at IS NULL LIMIT 1`, [workspaceId, userId]);
    return !!r.results[0];
}
/**
 * The legal reviewer who receives the proposal: the `legal_reviewer` on the
 * AI Regulatory program, else any current `legal_reviewer` in the workspace,
 * else the person onboarding (an owner / admin) so it is never unowned.
 * (Kept here for existing callers; obligation-source-service owns it.)
 */
export async function resolveLegalReviewer(db, workspaceId, fallbackUserId, now = new Date()) {
    return resolveReviewer(db, workspaceId, fallbackUserId, now);
}
/** Onboarding states → jurisdiction codes as `jurisdiction_rules` spells them. */
const STATE_JURISDICTION = {
    ca: 'US-CA', co: 'US-CO', nyc: 'US-NY-NYC', ny: 'US-NY', tx: 'US-TX', il: 'US-IL', other_us: null,
};
export async function recordFirstSystemIntake(db, input) {
    const now = input.now ?? new Date();
    const ws = input.workspace_id;
    const name = await systemName(db, ws, input.system_id);
    if (!name)
        throw new OnboardingIntakeError('not_found', 'System not found.');
    const keys = input.programs.map((p) => p.program);
    if (new Set(keys).size !== keys.length)
        throw new OnboardingIntakeError('invalid', 'Each program can be chosen once.');
    // Fail before any write: the owner must be an active member (T17 is re-checked by the assignment service).
    if (!(await isActiveMember(db, ws, input.owner_user_id))) {
        throw new OnboardingIntakeError('invalid', 'The system owner must be an active member of this workspace.');
    }
    // 1. Facts (validates the system belongs to this workspace again, inside the boundary service).
    const system = await setSystemFacts(db, {
        workspace_id: ws, system_id: input.system_id, now,
        handles_federal_data: input.federal_data === 'unsure' ? null : input.federal_data === 'yes',
        ...(input.internet_reachable !== undefined ? { internet_reachable: input.internet_reachable } : {}),
        ...(input.providers !== undefined ? { providers: input.providers } : {}),
    });
    // 2. Owner → system_owner hat (T17 + membership checked by the assignment service).
    const current = await db.query(`SELECT user_id FROM assignments
      WHERE workspace_id = ? AND subject_kind = 'system' AND subject_id = ? AND responsibility = 'system_owner' AND effective_to IS NULL
      ORDER BY effective_from ASC LIMIT 1`, [ws, input.system_id]);
    await syncOwnerAssignment(db, {
        workspace_id: ws, subject: { kind: 'system', id: input.system_id }, responsibility: 'system_owner',
        user_id: input.owner_user_id, previous_user_id: current.results[0]?.user_id ?? null,
        actor_user_id: input.actor_user_id, reason: 'Named during onboarding', now, newId: input.newId,
    });
    // 3. Programs → active program + standing boundary membership.
    const programs = [];
    for (const choice of input.programs) {
        // The choice's program fields (report_type, certification_class, program options) override the manifest's add-program defaults.
        const chosen = Object.fromEntries(Object.entries(choice).filter(([k]) => k !== 'program' && k !== 'program_options'));
        const chosenOptions = choice.program_options ?? {};
        let profile = await getActiveProfile(db, ws, choice.program);
        let created = false;
        let existing = null;
        if (!profile) {
            profile = await createCertificationProfile(db, {
                workspace_id: ws, program: choice.program, created_by_user_id: input.actor_user_id,
                profile_id: input.newId?.() ?? generateRandomId('cert'),
                ...(getProgramManifest(choice.program) ? programCreateFields(getProgramManifest(choice.program), { ...chosen, ...chosenOptions }) : chosen),
            });
            created = true;
        }
        else if (Object.entries(chosen).some(([k, v]) => profile[k] !== v)) {
            existing = { report_type: profile.report_type ?? null, certification_class: profile.certification_class ?? null };
        }
        let boundary = 'added';
        try {
            await addToBoundary(db, {
                workspace_id: ws, profile_id: profile.profile_id, period_id: null, system_id: input.system_id,
                actor_user_id: input.actor_user_id, reason: 'Added during onboarding', now, newId: input.newId,
            });
        }
        catch (err) {
            if (err instanceof BoundaryError && err.code === 'conflict')
                boundary = 'already_in';
            else
                throw err;
        }
        programs.push({ program: choice.program, profile_id: profile.profile_id, created, existing_settings: existing, boundary });
    }
    // 4. Law answers / PHI / federal "not sure" → one proposal awaiting Legal (V98-4-1).
    const lines = legalProposalLines(input);
    let legal = null;
    if (lines.length) {
        const proposer = (await displayName(db, input.actor_user_id)) ?? 'onboarding';
        const ownerId = await resolveLegalReviewer(db, ws, input.actor_user_id, now);
        const law = input.law ?? {};
        const tracksLaw = keys.some((k) => programHas(k, (m) => m.features.legalDeterminations)) && (await getCeiling(db, ws)).allows_legislation;
        const jurisdictions = [...new Set([
                ...(law.states ?? []).map((st) => STATE_JURISDICTION[st]).filter((j) => !!j),
                ...(law.eu_role ? ['EU'] : []),
            ])];
        const proposal = await createProposal(db, {
            workspace_id: ws, source: 'onboarding', actor_user_id: input.actor_user_id, system_id: input.system_id,
            legislation_on: tracksLaw ? true : null,
            jurisdictions: tracksLaw ? jurisdictions : [],
            facts: { federal_data: input.federal_data, ...law },
            note: `Decide AI-law answers for ${name} · proposed by ${proposer}`, now, newId: input.newId,
        });
        legal = { proposal_id: proposal.decision_id, owner_user_id: ownerId, owner_name: await displayName(db, ownerId), lines };
    }
    else {
        // Answers that no longer need Legal withdraw the open onboarding proposal for this system — V98-7-3 L8: only
        // the caller's OWN proposal. Someone else's stays with Legal (who declines it with a reason if it's moot); a
        // re-post can't silently pull an item out of Legal's queue.
        const at = now.toISOString();
        const open = await db.query(`SELECT decision_id FROM obligation_source_decisions
        WHERE workspace_id = ? AND kind = 'proposal' AND source = 'onboarding' AND system_id = ? AND state = 'proposed' AND proposed_by = ?`, [ws, input.system_id, input.actor_user_id]);
        if (open.results.length) {
            await db.batch(open.results.flatMap((o) => [
                {
                    sql: `UPDATE obligation_source_decisions SET state = 'withdrawn', resolved_by = ?, resolved_at = ?, resolved_reason = ?, updated_at = ?
                 WHERE decision_id = ? AND workspace_id = ? AND state = 'proposed'`,
                    params: [input.actor_user_id, at, 'Onboarding answers no longer need Legal', at, o.decision_id, ws],
                },
                aiProgramEvent(ws, { kind: 'obligation_source_decision', id: o.decision_id }, 'withdrawn', input.actor_user_id, at, { reason: 'Onboarding answers no longer need Legal' }),
            ]));
        }
    }
    return { system, owner_user_id: input.owner_user_id, programs, legal_proposal: legal };
}
//# sourceMappingURL=onboarding-intake-service.js.map